import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, ScrollText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { AUDIT_ACTIONS, type AuditAction, type AuditLog, type Property } from "@shared/schema";

const PAGE_SIZE = 50;

const ACTION_LABELS: Record<AuditAction, string> = {
  message_sent: "Message sent",
  task_created: "Task created",
  task_updated: "Task updated",
  form_submitted: "Form submitted",
  expense_logged: "Expense logged",
  vendor_paid: "Vendor paid",
  vendor_payment_voided: "Vendor payment voided",
  settings_changed: "Settings changed",
  member_profile_edited: "Member profile edited",
  rule_created: "Rule created",
  rule_modified: "Rule modified",
  task_batch_created: "Batch created",
  task_batch_dispatched: "Batch dispatched",
  task_batch_cancelled: "Batch cancelled",
  task_reminded: "Vendor reminded",
  task_duplicated: "Task duplicated",
  owner_approval_approved: "Owner approved cost",
  owner_approval_rejected: "Owner rejected cost",
};

function actionLabel(action: string): string {
  return (ACTION_LABELS as Record<string, string>)[action] ?? action;
}

type AuditLogResponse = { rows: AuditLog[]; total: number; page: number; limit: number };

type TeamMemberOption = {
  id: number;
  userId: number;
  inviteeUserId?: number | null;
  name?: string | null;
  email: string;
  role: string;
};

function buildAuditQueryString(filters: {
  actorUserId: string;
  action: string;
  propertyId: string;
  startDate: string;
  endDate: string;
  page?: number;
}): string {
  const params = new URLSearchParams();
  if (filters.actorUserId !== "all") params.set("actorUserId", filters.actorUserId);
  if (filters.action !== "all") params.set("action", filters.action);
  if (filters.propertyId !== "all") params.set("propertyId", filters.propertyId);
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.page) params.set("page", String(filters.page));
  return params.toString();
}

function formatTimestamp(value: AuditLog["createdAt"]): string {
  const d = value instanceof Date ? value : new Date(value as unknown as string);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function AuditLogTab({ properties }: { properties: Property[] }) {
  const { toast } = useToast();
  const [actorUserId, setActorUserId] = useState("all");
  const [action, setAction] = useState("all");
  const [propertyId, setPropertyId] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const filters = { actorUserId, action, propertyId, startDate, endDate };

  const propertyNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of properties) map.set(p.id, p.name);
    return map;
  }, [properties]);

  const membersQuery = useQuery<TeamMemberOption[]>({ queryKey: ["/api/app/team-members"] });
  const actorOptions = useMemo(() => {
    const list = membersQuery.data ?? [];
    return list
      .map((m) => ({
        value: String(m.role === "owner" ? m.userId : m.inviteeUserId ?? ""),
        label: m.name || m.email,
      }))
      .filter((o) => o.value !== "");
  }, [membersQuery.data]);

  const queryString = buildAuditQueryString({ ...filters, page });
  const auditUrl = queryString ? `/api/app/audit-logs?${queryString}` : "/api/app/audit-logs";
  const auditQuery = useQuery<AuditLogResponse>({ queryKey: [auditUrl] });

  const rows = auditQuery.data?.rows ?? [];
  const total = auditQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetToFirstPage = () => setPage(1);

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportQuery = buildAuditQueryString(filters);
      const url = exportQuery
        ? `/api/app/audit-logs/export.csv?${exportQuery}`
        : "/api/app/audit-logs/export.csv";
      const res = await apiRequest("GET", url);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast({
        title: "Export failed",
        description: parseErrorMessage(err, "Could not export the audit log. Please try again."),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Team member</label>
          <div className="w-[180px]">
            <Select
              value={actorUserId}
              onValueChange={(v) => {
                setActorUserId(v);
                resetToFirstPage();
              }}
            >
              <SelectTrigger data-testid="select-audit-actor">
                <SelectValue placeholder="All members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                {actorOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Action</label>
          <div className="w-[180px]">
            <Select
              value={action}
              onValueChange={(v) => {
                setAction(v);
                resetToFirstPage();
              }}
            >
              <SelectTrigger data-testid="select-audit-action">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {AUDIT_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>{actionLabel(a)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Property</label>
          <div className="w-[180px]">
            <SearchableSelect
              value={propertyId}
              onValueChange={(v) => {
                setPropertyId(v);
                resetToFirstPage();
              }}
              placeholder="All properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId="select-audit-property"
              options={[
                { value: "all", label: "All properties" },
                ...properties.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  keywords: p.address ? `${p.name} ${p.address}` : undefined,
                })),
              ]}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From</label>
          <Input
            type="date"
            className="w-[160px]"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              resetToFirstPage();
            }}
            data-testid="input-audit-start-date"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To</label>
          <Input
            type="date"
            className="w-[160px]"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              resetToFirstPage();
            }}
            data-testid="input-audit-end-date"
          />
        </div>

        <Button
          variant="outline"
          onClick={handleExport}
          disabled={exporting}
          className="ml-auto"
          data-testid="button-audit-export"
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {auditQuery.isLoading ? (
            <div className="p-4 space-y-3" data-testid="audit-log-loading">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : auditQuery.isError ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="audit-log-error">
              Could not load the audit log. Please try again.
            </div>
          ) : rows.length === 0 ? (
            <div
              className="p-12 flex flex-col items-center justify-center text-center text-gray-500"
              data-testid="audit-log-empty"
            >
              <ScrollText className="h-10 w-10 mb-3 text-gray-300" />
              <p className="font-medium text-gray-700">No activity yet</p>
              <p className="text-sm mt-1">Team actions will appear here as they happen.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Who</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Details</th>
                    <th className="px-4 py-3 font-medium">Property</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0" data-testid={`row-audit-${row.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500" data-testid={`text-audit-when-${row.id}`}>
                        {formatTimestamp(row.createdAt)}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900" data-testid={`text-audit-who-${row.id}`}>
                        {row.actorName}
                      </td>
                      <td className="px-4 py-3" data-testid={`text-audit-action-${row.id}`}>
                        <span className="inline-flex items-center rounded-full bg-brand-muted px-2.5 py-0.5 text-xs font-medium text-brand-muted-foreground">
                          {actionLabel(row.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-testid={`text-audit-entity-${row.id}`}>
                        {row.entityLabel || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-testid={`text-audit-property-${row.id}`}>
                        {row.propertyId != null
                          ? propertyNameById.get(row.propertyId) ?? `Property ${row.propertyId}`
                          : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span data-testid="text-audit-total">
            {total} {total === 1 ? "event" : "events"}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="button-audit-prev"
            >
              Previous
            </Button>
            <span data-testid="text-audit-page">Page {page} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="button-audit-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
